export type LatLon = { latitude: number; longitude: number };

/**
 * 기상청 DFS 격자(nx, ny) -> 위경도 역변환 (Lambert Conformal Conic, 기상청 표준).
 *
 * Ported from the legacy dashboard `_wxDfsToLatLon`. The legacy comment records why it exists:
 * sites are configured with 기상청 grid coordinates, so converting them here lets another factory
 * be added without touching the weather client. Constants are the 기상청 published projection
 * parameters and must not be tuned.
 */
export function dfsGridToLatLon(nx: number, ny: number): LatLon | null {
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  const RE = 6371.00877;
  const GRID = 5.0;
  const SLAT1 = 30.0;
  const SLAT2 = 60.0;
  const OLON = 126.0;
  const OLAT = 38.0;
  const XO = 43;
  const YO = 136;
  const DEGRAD = Math.PI / 180.0;
  const re = RE / GRID;
  const slat1 = SLAT1 * DEGRAD;
  const slat2 = SLAT2 * DEGRAD;
  const olon = OLON * DEGRAD;
  const olat = OLAT * DEGRAD;
  let sn = Math.tan(Math.PI * 0.25 + slat2 * 0.5) / Math.tan(Math.PI * 0.25 + slat1 * 0.5);
  sn = Math.log(Math.cos(slat1) / Math.cos(slat2)) / Math.log(sn);
  let sf = Math.tan(Math.PI * 0.25 + slat1 * 0.5);
  sf = (Math.pow(sf, sn) * Math.cos(slat1)) / sn;
  let ro = Math.tan(Math.PI * 0.25 + olat * 0.5);
  ro = (re * sf) / Math.pow(ro, sn);
  const xn = nx - XO;
  const yn = ro - ny + YO;
  let ra = Math.sqrt(xn * xn + yn * yn);
  if (sn < 0) ra = -ra;
  let alat = Math.pow((re * sf) / ra, 1.0 / sn);
  alat = 2.0 * Math.atan(alat) - Math.PI * 0.5;
  let theta: number;
  if (Math.abs(xn) <= 0.0) theta = 0.0;
  else if (Math.abs(yn) <= 0.0) theta = xn < 0 ? -Math.PI * 0.5 : Math.PI * 0.5;
  else theta = Math.atan2(xn, yn);
  const alon = theta / sn + olon;
  const latitude = alat / DEGRAD;
  const longitude = alon / DEGRAD;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}
